/**
 * Messaging rail tabs — Messages · Flows · Delivery.
 * Delivery is gated on delivery.read, matching the route guard in routeTable.
 * The delivery badge derives from the LIVE listMessageHistory op (docket
 * 2026-07-18 §2.1b) — the fictional DELIVERY_ROWS constant is deleted.
 */
import { useEffect, useState } from "react";
import type { NavApi } from "../../shell/router";
import { can, useTenant } from "../../shell/access";
import { useMessaging } from "./useMessaging";
import { useFlows } from "./useFlows";
import { fetchMessageHistory } from "./message-history-api";
import { listApptMessageTemplates } from "../appointments/appointments-api";
import { t as i18nT } from "../../lib/i18n";

export interface MessagingCounts { messages: number; flows: number; delivery: number; apptComms: number; }

let _deliveryCount: number | null = null; // per-session cache of the real op's row count
let _apptCommsCount: number | null = null; // per-session cache — booking-comms template count

/** Live tab counts for the Messaging rail (composes the feature data hooks). */
export function useMessagingCounts(): MessagingCounts {
  const slug = useTenant()?.slug ?? null;
  const messages = useMessaging().all().length;
  const flows = useFlows().all().length;
  const [delivery, setDelivery] = useState(_deliveryCount ?? 0);
  const [apptComms, setApptComms] = useState(_apptCommsCount ?? 0);
  // Cross-tenant leak fix (docket 2026-07-26): these counts are tenant-derived
  // and this hook backs an always-mounted rail badge, so an in-place workspace
  // switch must purge + refetch — keying the effect on the tenant slug (rather
  // than the old "already have a count" guard, which never re-ran) does that.
  useEffect(() => {
    _deliveryCount = null; _apptCommsCount = null;
    setDelivery(0); setApptComms(0);
    let live = true;
    if (can("delivery.read")) {
      fetchMessageHistory({ limit: 200 })
        .then((r) => { _deliveryCount = (r.data || []).length; if (live) setDelivery(_deliveryCount); })
        .catch(() => { /* badge stays 0 — the Delivery screen owns the real error state */ });
    }
    if (can("appointments.manage")) {
      // Booking comms tab (APT-08) — count is email+SMS templates from the REAL op.
      listApptMessageTemplates()
        .then((r) => { _apptCommsCount = (r.email || []).length + (r.sms || []).length; if (live) setApptComms(_apptCommsCount); })
        .catch(() => { /* badge stays 0 — the Booking comms screen owns the real error state */ });
    }
    return () => { live = false; };
  }, [slug]);
  return { messages, flows, delivery, apptComms };
}

export function messagingTabs(nav: NavApi, _active: string, counts: MessagingCounts) {
  const tabs = [
    { id: "messages", label: i18nT("messaging.tabs.messages", "Messages"), count: counts.messages, onClick: () => nav.go("messaging") },
  ];
  if (can("flows.read")) {
    tabs.push({ id: "flows", label: i18nT("messaging.tabs.messageFlows", "Message flows"), count: counts.flows, onClick: () => nav.go("messaging.flows") });
  }
  if (can("delivery.read")) {
    tabs.push({ id: "delivery", label: i18nT("messaging.tabs.messageDelivery", "Message delivery"), count: counts.delivery, onClick: () => nav.go("messaging.delivery") });
  }
  if (can("appointments.manage")) {
    // APT-08 — appointment trigger templates live in the ONE messaging system.
    tabs.push({ id: "apptcomms", label: i18nT("messaging.tabs.bookingComms", "Booking comms"), count: counts.apptComms, onClick: () => nav.go("messaging.appointments") });
  }
  return tabs;
}
